import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";

// AppClaudeToken - the rules of the owner's "Claude analysis" panel (`#claude`). No DOM, no
// `XMLHttpRequest`, no timers - `AppMain` does the I/O, `AppClaudeView` the markup.
//
// ==================================================================================================
// !! ⛔ OWNER ONLY, AND NOT SUBSCRIBER SIGN-IN (owner request 2026-09-25)
// ==================================================================================================
//
// The owner pastes the token `claude setup-token` printed; the service checks it with Claude and
// keeps it in Key Vault for the analysis of the OWNER'S conversations. The link to the panel is shown
// only when `/folder` marked this browser as the owner's (`owner: true`), and the service refuses
// everybody else with one neutral sentence. ⛔ CLAUDE.md's "Subscriber Claude sign-in: waiting on
// Anthropic" ruling is why this must never grow into a sign-in for subscribers.
//
// ⛔ THE TOKEN IS NEVER KEPT BY THE PAGE. It is read from the box at the moment Save is pressed, the
// box is emptied at once, and it exists afterwards only inside the one request body. Not in
// `AppClaudeState`, not in `localStorage`, not in the URL, not in a log.

/** What the panel shows. ⛔ No field here ever holds the token. */
export class AppClaudeState {
    /** A request is out. */
    public Busy: boolean = false;
    /** The service has answered a status for THIS browser, so the form may be shown. */
    public Known: boolean = false;
    public Stored: boolean = false;
    /** ISO time the stored token was set, or "". */
    public UpdatedAt: string = "";
    /** A sentence to show in ink - validation or a refusal. */
    public Why: string = "";
    /** A save just landed - the status line says so. */
    public JustSaved: boolean = false;
}

/** One answer from `/owner/claude-token`, read. */
export class AppClaudeAnswer {
    public Ok: boolean = false;
    /** The answer carried status fields (`stored`) - a status reply rather than a save. */
    public HasStatus: boolean = false;
    public Stored: boolean = false;
    public UpdatedAt: string = "";
    public StoredAt: string = "";
    public Why: string = "";
}

export class AppClaudeToken {
    public static readonly Route: string = "claude";
    /** ⚠ Must match `ClaudeTokenRules.Route` - `AppClaudeToken.test.ts` pins the two together. */
    public static readonly Path: string = "/owner/claude-token";
    /** ⚠ Must match `ClaudeTokenRules.Prefix`. */
    public static readonly Prefix: string = "sk-ant-oat01-";

    /** The status request. "" when this browser has nothing to prove itself with. */
    public static StatusBody = (Phone: string, Key: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(Phone).trim().length === 0 || nz(Key).trim().length === 0) { return ""; }
            return JSON.stringify({ number: nz(Phone).trim(), key: nz(Key).trim(), action: "status" });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppClaudeToken.StatusBody");
            return "";
        }
    };

    /** The save request - the ONLY place the token is written into anything. */
    public static SaveBody = (Phone: string, Key: string, Token: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(Phone).trim().length === 0 || nz(Key).trim().length === 0) { return ""; }
            return JSON.stringify({ number: nz(Phone).trim(), key: nz(Key).trim(), action: "save",
                token: nz(Token).trim() });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppClaudeToken.SaveBody");
            return "";
        }
    };

    /**
     * "" when the pasted text is worth sending; otherwise the sentence. ⚠ A FIRST PASS ONLY - the
     * service checks the full shape and then asks Claude. ⛔ The sentence never quotes the text.
     */
    public static ShapeWhy = (Token: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const T: string = nz(Token).trim();
            if (T.length === 0) {
                return "Paste the token first - the long line `claude setup-token` prints, starting"
                    + " sk-ant-oat01-.";
            }
            if (T.indexOf(AppClaudeToken.Prefix) !== 0) {
                return "That is not a Claude Code token. It starts sk-ant-oat01- - copy the line"
                    + " `claude setup-token` prints.";
            }
            if (/\s/.test(T) === true) {
                return "That token has a space or a line break inside it. Copy it again as one line.";
            }
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppClaudeToken.ShapeWhy");
            return "";
        }
    };

    /**
     * Read whatever came back. ⛔ THE BODY IS READ WHATEVER THE STATUS - the service states refusals
     * in it (`AppSignUpApi.Answer`'s rule); status 0 is a lost connection and says `Ours`.
     */
    public static Read = (Status: number, Text: string, Ours: string): AppClaudeAnswer => {
        if (getCoreUtils().KErrorMode === true) { return AppClaudeToken.No(Ours); }
        try {
            if (Status <= 0) { return AppClaudeToken.No(Ours); }
            const Raw: Record<string, unknown> = JSON.parse(nz(Text)) as Record<string, unknown>;
            if (Raw === null || typeof Raw !== "object") { return AppClaudeToken.No(Ours); }
            const Out: AppClaudeAnswer = new AppClaudeAnswer();
            Out.Ok = Raw.ok === true;
            Out.Why = nz(Raw.why as string);
            Out.HasStatus = typeof Raw.stored === "boolean";
            Out.Stored = Raw.stored === true;
            Out.UpdatedAt = nz(Raw.updatedAt as string);
            Out.StoredAt = nz(Raw.storedAt as string);
            if (Out.Ok === false && Out.Why.length === 0) { Out.Why = Ours; }
            return Out;
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: an unreadable answer on a public wire is an EXPECTED
            // condition (law 1, fate 1) - a proxy page, a truncated reply - shown as "our end".
            return AppClaudeToken.No(Ours);
        }
    };

    /** Fold a status answer into the state. */
    public static TakeStatus = (State: AppClaudeState, Answer: AppClaudeAnswer): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            State.Busy = false;
            State.JustSaved = false;
            if (Answer.Ok === true && Answer.HasStatus === true) {
                State.Known = true;
                State.Stored = Answer.Stored;
                State.UpdatedAt = Answer.UpdatedAt;
                State.Why = "";
                return;
            }
            State.Why = Answer.Why;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppClaudeToken.TakeStatus");
        }
    };

    /** Fold a save answer into the state. ⚠ A refusal leaves the status line as it was. */
    public static TakeSave = (State: AppClaudeState, Answer: AppClaudeAnswer): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            State.Busy = false;
            if (Answer.Ok === true) {
                State.Stored = true;
                State.UpdatedAt = Answer.StoredAt;
                State.JustSaved = true;
                State.Why = "";
                return;
            }
            State.JustSaved = false;
            State.Why = Answer.Why;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppClaudeToken.TakeSave");
        }
    };

    /** "Stored on 25 Sept 2026, 06:12" / "No token stored yet", or "" before the service answered. */
    public static StatusLine = (State: AppClaudeState): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (State.Known !== true) { return ""; }
            if (State.Stored !== true) { return "No token stored yet."; }
            const When: string = AppClaudeToken.DateSay(State.UpdatedAt);
            let Lead: string = "Stored";
            if (State.JustSaved === true) { Lead = "Checked with Claude and stored"; }
            if (When.length === 0) { return Lead + "."; }
            return Lead + " on " + When + ".";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppClaudeToken.StatusLine");
            return "";
        }
    };

    /**
     * An ISO time, in the reader's own locale, or "".
     * DOCUMENTED EXCEPTION: pure formatter. ERROR-SUPPRESSED-JUSTIFIED: "" IS the answer for a bad date.
     */
    public static DateSay = (Iso: string): string => {
        try {
            const At: number = Date.parse(nz(Iso));
            if (isNaN(At) === true) { return ""; }
            return new Date(At).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric",
                hour: "2-digit", minute: "2-digit" });
        } catch (e) {
            return "";
        }
    };

    /** Does the footer offer the panel? ⛔ Only with a key in hand AND `/folder`'s `owner: true`. */
    public static ShowLink = (Held: boolean, Owner: boolean): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return Held === true && Owner === true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppClaudeToken.ShowLink");
            return false;
        }
    };

    /** DOCUMENTED EXCEPTION: builds a refusal from a sentence in hand. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static No = (Why: string): AppClaudeAnswer => {
        const Out: AppClaudeAnswer = new AppClaudeAnswer();
        Out.Why = nz(Why);
        return Out;
    };
}
